import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Shipping Protection (Apex)
 *
 * Auto-enables a $4.99 shipping-protection line item once the cart has real
 * products, and lets the customer toggle it on/off. The customer's choice is
 * persisted in a cart attribute ("shipping_protection") so we never fight a
 * shopper who removes it:
 *
 *   ''        -> untouched; auto-enable on first real item, then mark 'added'
 *   'added'   -> protection is on (kept at qty 1). If the shopper removes it
 *                anywhere, we record 'declined' instead of re-adding.
 *   'declined'-> stay off; never auto-add.
 *
 * When the cart has no real items we reset back to '' so a fresh cart
 * auto-enables again.
 */

const ATTR = 'shipping_protection';
const SOURCE = 'shipping-protection';

// Single in-flight lock shared across instances (drawer + page never coexist,
// but this keeps us safe regardless).
let busy = false;

class ShippingProtectionComponent extends HTMLElement {
  connectedCallback() {
    this.variantId = Number(this.dataset.variantId);
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.checkbox?.addEventListener('change', this.#onToggle, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Auto-enable / reconcile on first render.
    this.#evaluate();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  #onCartUpdate = (event) => {
    // Ignore the events we dispatch ourselves to avoid loops.
    if (event?.detail?.sourceId === SOURCE) return;
    this.#evaluate();
  };

  #onToggle = async () => {
    if (!this.checkbox) return;
    const wantOn = this.checkbox.checked;
    if (busy) {
      // Revert optimistic UI while another op is running; next render fixes it.
      this.checkbox.checked = !wantOn;
      return;
    }
    if (wantOn) {
      await this.#enable();
    } else {
      await this.#disable();
    }
  };

  async #getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async #post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  #notify(cart) {
    document.dispatchEvent(new CartUpdateEvent(cart || {}, SOURCE, {}));
  }

  async #evaluate() {
    if (busy || !this.variantId) return;
    busy = true;
    let changed = false;
    let cart = null;

    try {
      cart = await this.#getCart();
      const vid = this.variantId;
      const items = cart.items || [];
      const prot = items.find((i) => i.variant_id === vid);
      const realCount = items.filter((i) => i.variant_id !== vid).length;
      const attr = (cart.attributes && cart.attributes[ATTR]) || '';

      if (realCount === 0) {
        // No real products: drop protection and reset so it can auto-enable later.
        if (prot || attr) {
          cart = await this.#post('/cart/update.js', {
            updates: prot ? { [vid]: 0 } : {},
            attributes: { [ATTR]: '' },
          });
          changed = true;
        }
      } else if (attr === '') {
        // First time we see real items -> auto-enable.
        if (!prot) {
          await this.#post('/cart/add.js', { items: [{ id: vid, quantity: 1 }] });
        }
        cart = await this.#post('/cart/update.js', { attributes: { [ATTR]: 'added' } });
        changed = true;
      } else if (attr === 'added') {
        if (!prot) {
          // Shopper removed it -> respect the choice from now on.
          cart = await this.#post('/cart/update.js', { attributes: { [ATTR]: 'declined' } });
          changed = true;
        } else if (prot.quantity !== 1) {
          cart = await this.#post('/cart/update.js', { updates: { [vid]: 1 } });
          changed = true;
        }
      } else if (attr === 'declined') {
        if (prot) {
          cart = await this.#post('/cart/update.js', { updates: { [vid]: 0 } });
          changed = true;
        }
      }
    } catch (error) {
      console.error('[shipping-protection] evaluate failed', error);
    } finally {
      busy = false;
    }

    if (changed) this.#notify(cart);
  }

  async #enable() {
    if (busy || !this.variantId) return;
    busy = true;
    let cart = null;
    try {
      const vid = this.variantId;
      const current = await this.#getCart();
      const has = (current.items || []).some((i) => i.variant_id === vid);
      if (!has) {
        await this.#post('/cart/add.js', { items: [{ id: vid, quantity: 1 }] });
      }
      cart = await this.#post('/cart/update.js', { attributes: { [ATTR]: 'added' } });
    } catch (error) {
      console.error('[shipping-protection] enable failed', error);
    } finally {
      busy = false;
    }
    this.#notify(cart);
  }

  async #disable() {
    if (busy || !this.variantId) return;
    busy = true;
    let cart = null;
    try {
      const vid = this.variantId;
      cart = await this.#post('/cart/update.js', {
        updates: { [vid]: 0 },
        attributes: { [ATTR]: 'declined' },
      });
    } catch (error) {
      console.error('[shipping-protection] disable failed', error);
    } finally {
      busy = false;
    }
    this.#notify(cart);
  }
}

if (!customElements.get('shipping-protection-component')) {
  customElements.define('shipping-protection-component', ShippingProtectionComponent);
}
